import * as crypto from "crypto";
import type { StorageReadDisposition, StorageReadGrant, StorageReadGrantInput } from "@gencow/core";

type GrantPayload = {
  v: 1;
  appName: string;
  storageId: string;
  exp: number;
  disposition: StorageReadDisposition;
  subject?: string;
  sessionId?: string;
};

export type StorageReadGrantVerification =
  | { ok: true; payload: GrantPayload }
  | { ok: false; reason: "missing" | "malformed" | "invalid" | "expired" | "app_mismatch" | "storage_mismatch" };

function base64UrlEncode(input: Buffer | string): string {
  return Buffer.from(input).toString("base64url");
}

function base64UrlDecode(input: string): string {
  return Buffer.from(input, "base64url").toString("utf-8");
}

function signPayload(encodedPayload: string, secret: string): string {
  return crypto.createHmac("sha256", secret).update(encodedPayload).digest("base64url");
}

function normalizeTtlSeconds(ttlSeconds: unknown): number {
  const value = typeof ttlSeconds === "number" && Number.isFinite(ttlSeconds) ? ttlSeconds : 60;
  return Math.max(1, Math.min(Math.floor(value), 300));
}

function normalizeDisposition(value: unknown): StorageReadDisposition {
  return value === "attachment" ? "attachment" : "inline";
}

export function createStorageReadGrant(params: {
  appName: string;
  storageId: string;
  secret: string;
  input?: StorageReadGrantInput;
  nowMs?: number;
}): StorageReadGrant {
  const nowMs = params.nowMs ?? Date.now();
  const payload: GrantPayload = {
    v: 1,
    appName: params.appName,
    storageId: params.storageId,
    exp: nowMs + normalizeTtlSeconds(params.input?.ttlSeconds) * 1000,
    disposition: normalizeDisposition(params.input?.disposition),
    ...(params.input?.subject ? { subject: params.input.subject } : {}),
    ...(params.input?.sessionId ? { sessionId: params.input.sessionId } : {}),
  };
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const signature = signPayload(encodedPayload, params.secret);
  return {
    url: `/api/storage/${encodeURIComponent(params.storageId)}?grant=${encodedPayload}.${signature}`,
    expiresAt: new Date(payload.exp).toISOString(),
  };
}

export function verifyStorageReadGrant(params: {
  token: string | null | undefined;
  appName: string;
  storageId: string;
  secret: string;
  nowMs?: number;
}): StorageReadGrantVerification {
  if (!params.token) return { ok: false, reason: "missing" };
  const [encodedPayload, signature, extra] = params.token.split(".");
  if (!encodedPayload || !signature || extra !== undefined) return { ok: false, reason: "malformed" };
  const expected = signPayload(encodedPayload, params.secret);
  const actual = Buffer.from(signature);
  if (actual.length !== Buffer.byteLength(expected)) return { ok: false, reason: "invalid" };
  if (!crypto.timingSafeEqual(actual, Buffer.from(expected))) return { ok: false, reason: "invalid" };
  let payload: GrantPayload;
  try {
    payload = JSON.parse(base64UrlDecode(encodedPayload)) as GrantPayload;
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (payload.v !== 1 || typeof payload.exp !== "number") return { ok: false, reason: "malformed" };
  if (payload.appName !== params.appName) return { ok: false, reason: "app_mismatch" };
  if (payload.storageId !== params.storageId) return { ok: false, reason: "storage_mismatch" };
  if (payload.exp <= (params.nowMs ?? Date.now())) return { ok: false, reason: "expired" };
  return { ok: true, payload };
}
